import { useMemo, useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import { useData } from '../lib/data.jsx';
import { useUI, Pimg } from '../lib/ui.jsx';
import { rpc } from '../lib/supabase.js';
import { B, thDate, todayISO } from '../lib/util.js';
import { COND } from '../lib/calc.js';
import { SheetHead } from '../lib/print.jsx';

const REF_LABEL = { cash: 'เงินสด', transfer: 'โอนเข้าบัญชี', preauth: 'ยกเลิกกันวงเงินบัตร' };

export default function Return({ go }) {
  const { can, settings, me } = useAuth();
  const { openContracts, itemsOf, refresh } = useData();
  const { toast, openSheet, closeSheet } = useUI();

  const [cid, setCid] = useState('');
  const [grades, setGrades] = useState({});       // item_id → 0..3
  const [fees, setFees] = useState({});           // item_id → override fee (ซ่อมได้)
  const [refMethod, setRefMethod] = useState('cash');
  const [refAcct, setRefAcct] = useState('');
  const [refAmt, setRefAmt] = useState('');
  const [busy, setBusy] = useState(false);

  const contract = openContracts.find(c => c.id === (cid || openContracts[0]?.id));
  const items = contract ? itemsOf(contract.id) : [];
  const latePct = Number(settings.late_fee_pct ?? 0.35);

  const calc = useMemo(() => {
    if (!contract) return null;
    let ded = 0, tripTotal = 0;
    const detail = items.map(it => {
      const g = grades[it.id] ?? 0;
      let fee = 0;
      const retail = Number(it.retail_snapshot);
      if (g === 1) fee = Math.min(Math.max(Number(fees[it.id] ?? 300), 200), 500);
      else if (g === 2) fee = Math.min(Number(fees[it.id] ?? Math.round(retail * .1)), Math.round(retail * .3));
      else if (g === 3) fee = Math.round(retail * .6);
      ded += fee; tripTotal += Number(it.trip_rate_snapshot);
      return { ...it, grade: g, fee };
    });
    const over = Math.max(0, contract.over_days);
    const late = over > 0 ? over * Math.round(tripTotal * latePct) : 0;
    const dep = Number(contract.deposit);
    const due = Math.max(0, dep - ded - late);
    const short = ded + late > dep ? ded + late - dep : 0;
    return { detail, ded, late, over, dep, due, short };
  }, [contract, items, grades, fees, latePct]);

  const refundVal = refAmt === '' ? (calc?.due ?? 0) : +refAmt;
  const diff = calc ? refundVal - calc.due : 0;
  const offSystem = Math.abs(diff) >= 1;

  const setGrade = (id, g) => { setGrades(x => ({ ...x, [id]: g })); setRefAmt(''); };

  const submit = () => {
    if (!contract || !calc) { toast('เลือกสัญญาก่อน'); return; }
    if (refMethod === 'transfer' && !refAcct.trim()) { toast('ระบุบัญชีผู้รับหรือเลขอ้างอิงการโอนก่อน'); return; }
    if (offSystem && !can('refund.deposit')) { toast('ยอดคืนต่างจากที่ระบบคำนวณ — ต้องใช้สิทธิ์อนุมัติคืนเงินประกัน'); return; }
    confirmClose();
  };

  // ---------- หน้ายืนยันก่อนปิดสัญญา ----------
  const confirmClose = () => {
    openSheet(<ConfirmSheet contract={contract} calc={calc} refundVal={refundVal}
      refMethod={refMethod} refAcct={refAcct} diff={diff} me={me}
      onCancel={closeSheet} onConfirm={doClose} busy={busy} />);
  };

  const doClose = async () => {
    setBusy(true);
    try {
      const res = await rpc('rp_close_contract', {
        p: {
          contract_id: contract.id,
          items: calc.detail.map(d => ({ item_id: d.id, grade: d.grade, damage_fee: d.fee })),
          refund_method: refMethod, refund_amount: refundVal,
          acct_ref: refAcct || null,
        },
      });
      closeSheet();
      toast(`ปิดสัญญา ${res.contract_no} · คืน ${B(res.refund)} ฿ (${REF_LABEL[refMethod]})` +
        (res.credit_amount ? ` · สร้างเครดิต ${B(res.credit_amount)} ฿` : ''));
      setGrades({}); setFees({}); setRefAmt(''); setRefAcct(''); setCid('');
      await refresh();
    } catch (e) { toast(e.message); }
    setBusy(false);
  };

  return (<>
    <h2>รับคืน / ตรวจสภาพ</h2>
    <div className="hint">เลือกสัญญา → ให้เกรดสภาพทีละชิ้น → ระบบคำนวณเงินคืนมัดจำและสร้างเครดิตเช่า→ซื้อ</div>

    <label className="fld">สัญญาเช่า</label>
    <select value={contract?.id || ''} onChange={e => { setCid(e.target.value); setGrades({}); setFees({}); setRefAmt(''); }}>
      {openContracts.map(c => <option key={c.id} value={c.id}>{c.contract_no} — {c.customer_name}{c.over_days > 0 ? ` · เกิน ${c.over_days} วัน` : ''}</option>)}
      {!openContracts.length && <option value="">— ไม่มีสัญญาค้าง —</option>}
    </select>

    <div style={{ marginTop: 14 }}>
      {calc?.detail.map(it => (
        <div className="insp" key={it.id}>
          <div className="top">
            <span className="nm" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="pthumb" style={{ width: 44, height: 38 }}><Pimg cat={it.category} size={32} image={it.image_url} /></span>
              <span>{it.name_snapshot} <span className="sku">({it.sku} · ชิ้น {it.unit_code})</span></span>
            </span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--muted)' }}>ราคาป้าย ณ วันทำสัญญา {B(it.retail_snapshot)}</span>
          </div>
          <div className="cond">
            {COND.map((cd, j) => (
              <button key={j} className={(grades[it.id] ?? 0) === j ? `on c${j}` : ''} onClick={() => setGrade(it.id, j)}>
                {cd.n}{j > 0 ? ` −${B(j === 1 ? 300 : j === 2 ? Math.round(it.retail_snapshot * .1) : Math.round(it.retail_snapshot * .6))}` : ''}
              </button>
            ))}
          </div>
          {(grades[it.id] === 1 || grades[it.id] === 2) && (
            <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', fontSize: 12.5 }}>
              <span>ค่าใช้จ่ายจริง ({grades[it.id] === 1 ? '200–500' : `ไม่เกิน ${B(Math.round(it.retail_snapshot * .3))}`})</span>
              <input type="number" className="mono-in" style={{ width: 110 }}
                value={fees[it.id] ?? (grades[it.id] === 1 ? 300 : Math.round(it.retail_snapshot * .1))}
                onChange={e => { setFees(x => ({ ...x, [it.id]: +e.target.value })); setRefAmt(''); }} />
            </div>
          )}
          {it.fee > 0 && <div className="fee show">หักจากมัดจำ −{B(it.fee)} ฿ {it.grade === 3 ? '(60% ของราคาป้าย ณ วันทำสัญญา)' : it.grade === 2 ? '(เพดาน 30% ของราคาป้าย)' : ''}</div>}
        </div>
      ))}
    </div>

    {contract && calc && (
      <div className="card" style={{ maxWidth: 460 }}>
        <h2>สรุปการคืน</h2>
        <div>
          <div className="row" style={rowS}><span>มัดจำที่ถือไว้</span><span className="mono">{B(calc.dep)}</span></div>
          <div className="row" style={rowS}><span>หักค่าสภาพ</span><span className="mono" style={{ color: calc.ded ? 'var(--rust)' : 'inherit' }}>−{B(calc.ded)}</span></div>
          <div className="row" style={rowS}><span>ค่าคืนช้า{calc.over > 0 ? ` (${calc.over} วัน × ${Math.round(latePct * 100)}%/คืน)` : ''}</span><span className="mono" style={{ color: calc.late ? 'var(--rust)' : 'inherit' }}>−{B(calc.late)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 12, fontWeight: 700, fontSize: 15 }}>
            <span>คืนลูกค้า</span><span className="mono" style={{ fontSize: 22, color: 'var(--gold)', fontWeight: 800 }}>{B(calc.due)} ฿</span>
          </div>
          {calc.short > 0 && <div className="note show" style={{ background: 'var(--rust-soft)', color: 'var(--rust)' }}>มัดจำไม่พอ — เรียกเก็บเพิ่ม {B(calc.short)} ฿</div>}
          {Number(contract.net_rent) > 0 &&
            <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--muted)' }}>เครดิตเช่า→ซื้อที่จะสร้าง: <b className="mono" style={{ color: 'var(--gold)' }}>{B(contract.net_rent)} ฿</b> · อายุ {Number(settings.credit_days ?? 15)} วัน</div>}
        </div>

        <div style={{ marginTop: 16, borderTop: '1px solid var(--line2)', paddingTop: 14 }}>
          <label className="fld" style={{ marginTop: 0 }}>วิธีคืนเงินประกัน</label>
          <div className="catfilter">
            {Object.entries(REF_LABEL).map(([k, n]) => (
              <button key={k} className={refMethod === k ? 'on' : ''} onClick={() => setRefMethod(k)}>{n}</button>
            ))}
          </div>
          {refMethod === 'transfer' && (
            <div style={{ marginBottom: 10 }}>
              <label className="mini">บัญชีผู้รับ / อ้างอิงการโอน (บังคับ)</label>
              <input style={inpS} value={refAcct} onChange={e => setRefAcct(e.target.value)} placeholder="ธนาคาร เลขบัญชี หรือเลขอ้างอิง" />
            </div>
          )}
          <label className="mini">จำนวนเงินที่คืนจริง (บาท)</label>
          <input type="number" className={'mono-in' + (offSystem ? ' warn-in' : '')} style={{ width: 140 }} min={0}
            value={refAmt === '' ? calc.due : refAmt} onChange={e => setRefAmt(e.target.value)} />
          <div className="hint" style={{ marginTop: 8 }}>
            {offSystem
              ? <b style={{ color: 'var(--gold)' }}>ต่างจากยอดที่ระบบคำนวณ {diff > 0 ? '+' : ''}{B(diff)} ฿ — ต้องใช้สิทธิ์อนุมัติคืนเงินประกัน และถูกบันทึกประวัติ</b>
              : <>ตรงกับยอดที่ระบบคำนวณ <b>{B(calc.due)} ฿</b></>}
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <button className="btn" disabled={busy || !navigator.onLine} onClick={submit}>ปิดสัญญา + คืนเงินประกัน</button>
        </div>
        <div className="hint" style={{ marginTop: 10 }}>ระบบจะเปิดหน้ายืนยันสรุปทุกอย่างก่อน ยังแก้ไขกลับได้</div>
      </div>
    )}
  </>);
}

const rowS = { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dashed var(--line)' };
const inpS = { width: '100%', border: 0, borderBottom: '1px solid var(--line2)', background: 'transparent', padding: '7px 2px', fontFamily: 'var(--sans)', fontSize: 14 };

function ConfirmSheet({ contract, calc, refundVal, refMethod, refAcct, diff, me, onCancel, onConfirm, busy }) {
  return (<>
    <SheetHead sub={me.branch_name || 'สาขาหลัก'} />
    <h1>ยืนยันปิดสัญญา</h1>
    <div style={{ fontSize: 11, color: '#8a8a8a', letterSpacing: 1 }}>CLOSE CONTRACT — CONFIRMATION</div>
    <div className="rule" />
    <div className="warnbox" style={{ marginBottom: 16, marginTop: 0 }}>
      <b>ตรวจสอบให้ครบก่อนกดยืนยัน</b> — เมื่อปิดสัญญาแล้ว อุปกรณ์จะกลับเข้าคลัง เงินประกันถูกตัดออกจากระบบ และสร้างเครดิตให้ลูกค้าทันที การแก้ไขย้อนหลังต้องใช้สิทธิ์ผู้ดูแลระบบ
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 26 }}>
      <div><h4>สัญญา</h4><div className="kv">
        <b>เลขที่</b><span className="mono">{contract.contract_no}</span>
        <b>ผู้เช่า</b><span>{contract.customer_name}</span>
        <b>กำหนดคืน</b><span>{thDate(contract.to_date)}</span>
        <b>สถานะ</b><span>{calc.over > 0 ? `เกินกำหนด ${calc.over} วัน` : 'ตรงกำหนด'}</span>
      </div></div>
      <div><h4>การคืนเงินประกัน</h4><div className="kv">
        <b>วิธีคืน</b><span>{REF_LABEL[refMethod]}</span>
        {refAcct ? <><b>บัญชี/อ้างอิง</b><span>{refAcct}</span></> : null}
        <b>ผู้รับเงิน</b><span>{contract.customer_name}</span>
      </div></div>
    </div>
    <h4>สภาพอุปกรณ์ที่ตรวจรับ ({calc.detail.length} ชิ้น)</h4>
    {calc.detail.map(d => (
      <div className="dw-item" key={d.id}>
        <div className="pthumb"><Pimg cat={d.category} size={30} image={d.image_url} /></div>
        <div style={{ flex: 1 }}>
          <div className="n">{d.name_snapshot} · {d.unit_code}</div>
          <div className="s">{COND[d.grade].n}{d.fee ? ' · หัก ' + B(d.fee) + ' ฿' : ''}</div>
        </div>
      </div>
    ))}
    <h4>ยอดเงิน</h4>
    <div style={{ maxWidth: 420 }}>
      <div className="tot"><span>เงินประกันที่ถือไว้</span><span className="mono">{B(calc.dep)}</span></div>
      <div className="tot"><span>หักค่าสภาพอุปกรณ์</span><span className="mono">−{B(calc.ded)}</span></div>
      <div className="tot"><span>หักค่าคืนล่าช้า</span><span className="mono">−{B(calc.late)}</span></div>
      <div className="tot"><span>ยอดที่ระบบคำนวณ</span><span className="mono">{B(calc.due)}</span></div>
      <div className="tot big"><span>คืนลูกค้าจริง</span><span className="mono">{B(refundVal)} ฿</span></div>
    </div>
    {Math.abs(diff) >= 1 && (
      <div className="warnbox"><b>ยอดคืนต่างจากที่ระบบคำนวณ {diff > 0 ? '+' : ''}{B(diff)} ฿</b><br />
        ต้องได้รับอนุมัติจากผู้มีสิทธิ์ "อนุมัติคืนเงินประกัน" และจะถูกบันทึกไว้ในประวัติการใช้งาน</div>
    )}
    {calc.short > 0 && (
      <div className="warnbox">เงินประกันไม่พอหักค่าเสียหาย — ต้องเรียกเก็บเพิ่มจากลูกค้า <b>{B(calc.short)} ฿</b></div>
    )}
    {Number(contract.net_rent) > 0 &&
      <div style={{ marginTop: 12, fontSize: 12.5, color: '#666' }}>เครดิตที่จะสร้างให้ลูกค้า <b>{B(contract.net_rent)} ฿</b> · ใช้ได้เฉพาะสินค้ารุ่นเดิม ภายใน 15 วัน</div>}
    <div className="noprint" style={{ marginTop: 22, display: 'flex', gap: 8 }}>
      <button className="btn" disabled={busy} onClick={onConfirm}>{busy ? 'กำลังบันทึก…' : 'ยืนยันปิดสัญญา'}</button>
      <button className="btn ghost" onClick={onCancel}>กลับไปแก้ไข</button>
    </div>
  </>);
}
